import type { SarCategoryKey, SarValidationError } from '../components/sar/SarInterfaces';
import SarTagCategoryMapper from '../components/sar/SarTagCategoryMapper';

class SarValidationCategoryMapper {
  public static getCategoriesWithErrors(errors: SarValidationError[]): Set<SarCategoryKey> {
    const categoriesWithErrors = new Set<SarCategoryKey>();

    if (!errors || errors.length === 0) {
      return categoriesWithErrors;
    }

    const invalidFieldIds = this.getInvalidFieldIds(errors);

    // Yükümlü
    if (invalidFieldIds.has('yukumlu')) {
      categoriesWithErrors.add('yukumlu');
    }

    // Temel Bilgiler
    if (
      invalidFieldIds.has('bildirimkategorisi') ||
      invalidFieldIds.has('yerelreferansno') ||
      invalidFieldIds.has('oncekibildirimreferansno') ||
      invalidFieldIds.has('aciliyet') ||
      invalidFieldIds.has('terororgutu')
    ) {
      categoriesWithErrors.add('temelbilgiler');
    }

    // Gerçek Kişiler
    const hasGercekKisiError = Array.from(invalidFieldIds).some((fieldId) => {
      if (fieldId.startsWith('hesap-')) {
        return false;
      }
      if (fieldId.startsWith('gercekkisi-')) {
        return true;
      }

      const isHesapSahibiField = ['adsoyadunvan', 'pasaport'].includes(fieldId);
      if (isHesapSahibiField) {
        return false;
      }

      const isCommonField = ['tckn', 'vkn', 'ykn', 'magdur'].includes(fieldId);
      if (isCommonField) {
        return false;
      }
      const category = SarTagCategoryMapper.mapTagToCategory(fieldId);
      return category === 'gercekkisiler';
    });
    if (hasGercekKisiError) {
      categoriesWithErrors.add('gercekkisiler');
    }

    // Diğer Kişiler
    const hasDigerKisiError = Array.from(invalidFieldIds).some((fieldId) => {
      if (fieldId.startsWith('hesap-')) {
        return false;
      }
      if (fieldId.startsWith('digerkisi-')) {
        return true;
      }

      const isCommonField = ['vkn', 'magdur'].includes(fieldId);
      if (isCommonField) {
        return false;
      }
      const category = SarTagCategoryMapper.mapTagToCategory(fieldId);
      return category === 'digerkisiler';
    });
    if (hasDigerKisiError) {
      categoriesWithErrors.add('digerkisiler');
    }

    // Şüpheli İşlemler
    const hasSupheliIslemError = Array.from(invalidFieldIds).some((fieldId) => {
      if (fieldId.startsWith('supheliislem-')) {
        return true;
      }
      const isCommonField = ['parabirimi'].includes(fieldId);
      if (isCommonField) {
        return false;
      }
      const category = SarTagCategoryMapper.mapTagToCategory(fieldId);
      return category === 'supheliislemler';
    });
    if (hasSupheliIslemError) {
      categoriesWithErrors.add('supheliislemler');
    }

    const hasHesapError = Array.from(invalidFieldIds).some((fieldId) => {
      if (fieldId.startsWith('hesap-')) {
        return true;
      }

      const isCommonField = ['tckn', 'vkn', 'ykn', 'magdur', 'adsoyadunvan', 'pasaport', 'parabirimi'].includes(fieldId);
      if (isCommonField) {
        return false;
      }
      const category = SarTagCategoryMapper.mapTagToCategory(fieldId);
      return category === 'hesaplar';
    });
    if (hasHesapError) {
      categoriesWithErrors.add('hesaplar');
    }

    // Şüphe Kategorileri
    const hasSupheKategorisiError = Array.from(invalidFieldIds).some((fieldId) => {
      if (fieldId.startsWith('suphekategorisi-')) {
        return true;
      }
      const category = SarTagCategoryMapper.mapTagToCategory(fieldId);
      return category === 'suphekategorileri';
    });
    if (hasSupheKategorisiError) {
      categoriesWithErrors.add('suphekategorileri');
    }

    // Bildirim Açıklama
    if (invalidFieldIds.has('bildirimaciklama')) {
      categoriesWithErrors.add('bildirimaciklama');
    }

    return categoriesWithErrors;
  }

  public static getInvalidXmlTags(errors: SarValidationError[]): Set<string> {
    const invalidTags = new Set<string>();

    errors.forEach((error) => {
      const tags = this.extractXmlTagsFromError(error);
      tags.forEach((tag) => invalidTags.add(tag));
    });
    return invalidTags;
  }

  public static getInvalidFieldIds(errors: SarValidationError[]): Set<string> {
    const invalidFieldIds = new Set<string>();

    errors.forEach((error) => {
      const fieldId = this.extractFieldIdFromError(error);
      if (fieldId) {
        invalidFieldIds.add(fieldId);
      } else {
        // console.warn('⚠️ SarValidationCategoryMapper - FieldId oluşturulamadı:', error.message);
      }
    });
    return invalidFieldIds;
  }

  private static extractFieldIdFromError(error: SarValidationError): string | null {
    const message = error.message.toLowerCase();

    if (message.includes('yükümlü') || message.includes('yukumlu')) {
      return 'yukumlu';
    }

    if ((message.includes('e-posta') || message.includes('eposta')) && (message.includes('cep telefonu') || message.includes('ceptelefonu'))) {
      const entityMatch = message.match(/(gerçek kişi|diğer kişi|şüpheli işlem) (\d+)/);
      if (entityMatch) {
        const entityType = this.getEntityTypeFromMatch(entityMatch[1]);
        const entityIndex = parseInt(entityMatch[2], 10) - 1;
        return `${entityType}-${entityIndex}-iletisimbilgileri`;
      }
    }

    const tags = this.extractXmlTagsFromError(error);
    if (tags.length === 0) {
      return null;
    }
    const tag = tags[0];

    const adresMatch = message.match(/(gerçek kişi|diğer kişi|şüpheli işlem) (\d+).*adres (\d+)/);
    if (adresMatch) {
      const entityTypeMatch = adresMatch[1];
      const entityType = this.getEntityTypeFromMatch(entityTypeMatch);
      const entityIndex = parseInt(adresMatch[2], 10) - 1;
      const adresIndex = parseInt(adresMatch[3], 10) - 1;
      return `${entityType}-${entityIndex}-adres-${adresIndex}-${tag}`;
    }

    const iletisimBilgileriGenelMatch = message.match(/(gerçek kişi|diğer kişi|şüpheli işlem) (\d+).*<iletisimbilgileri>/);
    if (iletisimBilgileriGenelMatch) {
      const entityTypeMatch = iletisimBilgileriGenelMatch[1];
      const entityType = this.getEntityTypeFromMatch(entityTypeMatch);
      const entityIndex = parseInt(iletisimBilgileriGenelMatch[2], 10) - 1;
      return `${entityType}-${entityIndex}-iletisimbilgileri`;
    }

    const iletisimMatch = message.match(/(gerçek kişi|diğer kişi|şüpheli işlem) (\d+).*iletişim bilgisi (\d+)/);
    if (iletisimMatch) {
      const entityTypeMatch = iletisimMatch[1];
      const entityType = this.getEntityTypeFromMatch(entityTypeMatch);
      const entityIndex = parseInt(iletisimMatch[2], 10) - 1;
      const iletisimIndex = parseInt(iletisimMatch[3], 10) - 1;
      if (tag === 'tip') {
        return `${entityType}-${entityIndex}-iletisimbilgisi-${iletisimIndex}-tip`;
      }
      return `${entityType}-${entityIndex}-iletisimbilgisi-${iletisimIndex}`;
    }

    const hesapMatch = message.match(/hesap (\d+)/);
    if (hesapMatch) {
      const hesapIndex = parseInt(hesapMatch[1], 10) - 1;
      const hesapSahibiMatch = message.match(/hesapsahibi (\d+)/);
      if (hesapSahibiMatch) {
        const ownerIndex = parseInt(hesapSahibiMatch[1], 10) - 1;
        return `hesap-${hesapIndex}-hesapsahibi-${ownerIndex}-${tag}`;
      }
      return `hesap-${hesapIndex}-${tag}`;
    }

    const gercekKisiMatch = message.match(/gerçek kişi (\d+)/);
    if (gercekKisiMatch) {
      const personIndex = parseInt(gercekKisiMatch[1], 10) - 1;
      const kimlikBelgesiMatch = message.match(/kimlik belgesi (\d+)/);
      if (kimlikBelgesiMatch) {
        const docIndex = parseInt(kimlikBelgesiMatch[1], 10) - 1;
        return `gercekkisi-${personIndex}-kimlikbelgesi-${docIndex}-${tag}`;
      }
      return `gercekkisi-${personIndex}-${tag}`;
    }

    const digerKisiMatch = message.match(/diğer kişi (\d+)/);
    if (digerKisiMatch) {
      if (message.includes('iletişim bilgisi') || message.includes('adres')) {
        return null;
      }
      const personIndex = parseInt(digerKisiMatch[1], 10) - 1;
      return `digerkisi-${personIndex}-${tag}`;
    }

    const supheliIslemMatch = message.match(/şüpheli işlem (\d+)/);
    if (supheliIslemMatch) {
      const islemIndex = parseInt(supheliIslemMatch[1], 10) - 1;
      return `supheliislem-${islemIndex}-${tag}`;
    }

    const supheKategorisiMatch = message.match(/şüphe kategorisi (\d+)/);
    if (supheKategorisiMatch) {
      const kategoriIndex = parseInt(supheKategorisiMatch[1], 10) - 1;
      return `suphekategorisi-${kategoriIndex}`;
    }

    return tag;
  }

  private static extractXmlTagsFromError(error: SarValidationError): string[] {
    const tags: string[] = [];
    const message = error.message;
    const tagRegex = /<([a-z]+)>/gi;
    const matches = message.matchAll(tagRegex);

    for (const match of matches) {
      const tagName = match[1].toLowerCase();
      tags.push(tagName);
    }

    if (tags.length === 0) {
      const tagName = this.extractTagNameFromMessage(message);
      if (tagName) {
        tags.push(tagName);
      }
    }
    return tags;
  }

  private static extractTagNameFromMessage(message: string): string | null {
    const lowerMessage = message.toLowerCase();

    const tagMappings: Record<string, string> = {
      yükümlü: 'yukumlu',
      yukumlu: 'yukumlu',
      'bildirim kategorisi': 'bildirimkategorisi',
      bildirimkategorisi: 'bildirimkategorisi',
      'yerel referans': 'yerelreferansno',
      yerelreferansno: 'yerelreferansno',
      'önceki bildirim referans': 'oncekibildirimreferansno',
      oncekibildirimreferansno: 'oncekibildirimreferansno',
      aciliyet: 'aciliyet',
      'terör örgütü': 'terororgutu',
      terororgutu: 'terororgutu',
      'bildirim açıklama': 'bildirimaciklama',
      bildirimaciklama: 'bildirimaciklama',
      tckn: 'tckn',
      vkn: 'vkn',
      ykn: 'ykn',
      adi: 'adi',
      soyadi: 'soyadi',
      dogumtarihi: 'dogumtarihi',
      dogumyeri: 'dogumyeri',
      anaadi: 'anaadi',
      babaadi: 'babaadi',
      uyrugu: 'uyrugu',
      cinsiyeti: 'cinsiyeti',
      meslek: 'meslek',
      ekbilgi: 'ekbilgi',
      kimlikbilgilerisahte: 'kimlikbilgilerisahte',
      magdur: 'magdur',
      kimliktespitturu: 'kimliktespitturu',
      islemlebaglantisi: 'islemlebaglantisi',
      islemlebaglantisiaciklama: 'islemlebaglantisiaciklama',
      kurulusnevi: 'kurulusnevi',
      unvan: 'unvan',
      isletmeadi: 'isletmeadi',
      sicilno: 'sicilno',
      faaliyetkonusu: 'faaliyetkonusu',
      ulkekodu: 'ulkekodu',
      ilkodu: 'ilkodu',
      ilcekodu: 'ilcekodu',
      kurulustarihi: 'kurulustarihi',
      websitesi: 'websitesi',
      islemcesidi: 'islemcesidi',
      islemtarihi1: 'islemtarihi1',
      islemtarihi2: 'islemtarihi2',
      islemdurumu: 'islemdurumu',
      hesababagli: 'hesababagli',
      tutartp: 'tutartp',
      tutar: 'tutar',
      parabirimi: 'parabirimi',
      islemturu: 'islemturu',
      islemkanali: 'islemkanali',
      ipv4: 'ipv4',
      port: 'port',
      islemaciklamasi: 'islemaciklamasi',
      islemingerceklestirildigiyer: 'islemingerceklestirildigiyer',
      islemintetiklendigiyer: 'islemintetiklendigiyer',
      islemearaciliketmedurumu: 'islemearaciliketmedurumu',
      islemearacilikedenkurum: 'islemearacilikedenkurum',
      sahisfirmasihesabi: 'sahisfirmasihesabi',
      hesapturu: 'hesapturu',
      hesapno: 'hesapno',
      iban: 'iban',
      acilistarihi: 'acilistarihi',
      kapanistarihi: 'kapanistarihi',
      sonislemtarihi: 'sonislemtarihi',
      bakiyetp: 'bakiyetp',
      bakiye: 'bakiye',
      kisitlamaturu: 'kisitlamaturu',
      kisitlamadigeraciklama: 'kisitlamadigeraciklama',
      adsoyadunvan: 'adsoyadunvan',
      pasaport: 'pasaport',
      suphekategorisi: 'suphekategorisi',
      'e-posta': 'iletisimbilgileri',
      eposta: 'iletisimbilgileri',
      'cep telefonu': 'iletisimbilgileri',
      ceptelefonu: 'iletisimbilgileri',
      'iletisim bilgisi': 'iletisimbilgisi',
      iletisimbilgisi: 'iletisimbilgisi',
      'iletisim bilgileri': 'iletisimbilgileri',
      iletisimbilgileri: 'iletisimbilgileri',
    };

    for (const [key, tag] of Object.entries(tagMappings)) {
      if (lowerMessage.includes(key)) {
        return tag;
      }
    }

    return null;
  }

  private static mapErrorToCategory(error: SarValidationError): SarCategoryKey | null {
    const message = error.message.toLowerCase();

    if (message.includes('yükümlü') || message.includes('yukumlu')) {
      return 'yukumlu';
    }

    if (message.includes('bildirim açıklama') || message.includes('bildirimaciklama')) {
      return 'bildirimaciklama';
    }

    if (
      message.includes('bildirim kategorisi') ||
      message.includes('bildirimkategorisi') ||
      message.includes('yerel referans') ||
      message.includes('yerelreferansno') ||
      message.includes('yerel referans no') ||
      message.includes('önceki bildirim referans') ||
      message.includes('oncekibildirimreferansno') ||
      message.includes('önceki bildirim referans no') ||
      message.includes('aciliyet') ||
      message.includes('terör örgütü') ||
      message.includes('terororgutu')
    ) {
      return 'temelbilgiler';
    }

    if (message.includes('hesap') && !message.includes('hesababagli')) {
      return 'hesaplar';
    }

    if (message.includes('gerçek kişi') || message.includes('gercekkisi')) {
      return 'gercekkisiler';
    }

    if (message.includes('diğer kişi') || message.includes('digerkisi')) {
      return 'digerkisiler';
    }

    if (message.includes('şüpheli işlem') || message.includes('supheliislem')) {
      return 'supheliislemler';
    }

    const xmlTags = this.extractXmlTagsFromError(error);
    if (xmlTags.length > 0) {
      const categoryFromTag = SarTagCategoryMapper.mapTagToCategory(xmlTags[0]);
      if (categoryFromTag) {
        return categoryFromTag;
      }
    }

    if (message.includes('şüphe kategorisi') || message.includes('suphekategorisi')) {
      return 'suphekategorileri';
    }

    if (message.includes('hesap') && !message.includes('hesababagli')) {
      return 'hesaplar';
    }

    return null;
  }

  private static getEntityTypeFromMatch(entityTypeMatch: string): string {
    const normalizedMatch = entityTypeMatch.toLowerCase();

    switch (true) {
      case normalizedMatch.includes('gerçek') || normalizedMatch.includes('gercek'):
        return 'gercekkisi';
      case normalizedMatch.includes('diğer') || normalizedMatch.includes('diger'):
        return 'digerkisi';
      case normalizedMatch.includes('şüpheli') || normalizedMatch.includes('supheli'):
        return 'supheliislem';
      default:
        return 'supheliislem';
    }
  }

}

export default SarValidationCategoryMapper;
